/**
 * Done. / ChiroCPD — OnboardingLayout
 *
 * Centred card max-w-lg, step progress dots, BrandFooter.
 * No TopNav — onboarding is a linear flow before the main app.
 */

import type { ReactNode } from 'react';
import { BrandFooter } from '@/components/ui/BrandFooter';
import { cx } from '@/lib/utils';

interface OnboardingLayoutProps {
  children: ReactNode;
  step: number;
  totalSteps: number;
}

export function OnboardingLayout({ children, step, totalSteps }: OnboardingLayoutProps): React.ReactElement {
  return (
    <div className="flex min-h-screen flex-col bg-[var(--surface-subtle)]">
      {/* Header strip */}
      <header className="bg-[var(--surface-base)] border-b border-[var(--border-subtle)] px-4 py-3" data-print-hidden>
        <div className="mx-auto max-w-lg flex items-center justify-between">
          <span className="font-bold text-[var(--color-done-700)] text-base">ChiroCPD</span>
          <span className="text-xs text-[var(--text-muted)]">
            Step {String(Math.min(step, totalSteps))} of {String(totalSteps)}
          </span>
        </div>
      </header>

      {/* Step progress dots */}
      <div
        className="flex justify-center gap-2 py-4"
        role="progressbar"
        aria-valuenow={step}
        aria-valuemin={1}
        aria-valuemax={totalSteps}
        aria-label={`Step ${String(step)} of ${String(totalSteps)}`}
        data-print-hidden
      >
        {Array.from({ length: totalSteps }).map((_, i) => {
          const isComplete = i + 1 < step;
          const isCurrent = i + 1 === step;
          return (
            <span
              key={i}
              aria-hidden="true"
              className={cx(
                'h-2 rounded-full transition-all',
                isCurrent  && 'w-6 bg-[var(--color-done-500)]',
                isComplete && 'w-2 bg-[var(--color-done-300)]',
                !isCurrent && !isComplete && 'w-2 bg-[var(--border-base)]',
              )}
            />
          );
        })}
      </div>

      {/* Content card */}
      <main
        className="flex-1 flex items-start justify-center px-4 py-6"
        aria-label="Onboarding survey"
      >
        <div className="w-full max-w-lg rounded-[var(--radius-lg)] bg-[var(--surface-base)] p-8 shadow-md">
          {children}
        </div>
      </main>

      <BrandFooter />
    </div>
  );
}
